import {
  AUTH_TOKEN_KEY,
  clearSession,
} from "@/modules/safecheck/auth";
import { workContextHeaders } from "@/modules/safecheck/workContext";

const DEFAULT_API_URL = "/api";

let redirectingToLogin = false;

function firstValidationError(payload: unknown): string | null {
  if (!payload || typeof payload !== "object" || !("errors" in payload)) {
    return null;
  }
  const errors = (payload as { errors?: Record<string, string[]> }).errors;
  if (!errors) return null;
  for (const messages of Object.values(errors)) {
    if (Array.isArray(messages) && messages[0]) {
      return String(messages[0]);
    }
  }
  return null;
}

function isAuthPublicPath(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname.startsWith("/login/") ||
    pathname === "/select-account" ||
    pathname.startsWith("/select-account/")
  );
}

/** Session expirée / token invalide → nettoyage + retour écran d'accueil (login). */
export function redirectToLoginAfterAuthLoss(): void {
  if (typeof window === "undefined") return;
  clearSession();
  if (redirectingToLogin) return;
  if (isAuthPublicPath(window.location.pathname)) return;
  redirectingToLogin = true;
  const next = `${window.location.pathname}${window.location.search}`;
  const qs = next && next !== "/" ? `?from=${encodeURIComponent(next)}` : "";
  window.location.assign(`/login${qs}`);
}

export function isUnauthenticatedMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  const normalized = message.trim().toLowerCase();
  return (
    normalized === "unauthenticated." ||
    normalized === "unauthenticated" ||
    normalized.includes("unauthenticated") ||
    normalized.includes("session expirée")
  );
}

/** Ne jamais afficher le texte brut Laravel « Unauthenticated. » dans l'UI. */
export function sanitizeApiErrorMessage(message: string | null | undefined): string | null {
  if (!message) return null;
  if (isUnauthenticatedMessage(message)) return null;
  const normalized = message.trim().toLowerCase();
  if (normalized.includes("post data is too large") || normalized.includes("content too large")) {
    return "Le fichier joint est trop volumineux. Réduisez le PDF (max. 20 Mo) puis réessayez.";
  }
  return message;
}

export function getApiBaseUrl(): string {
  const base = import.meta.env.VITE_API_URL ?? DEFAULT_API_URL;
  return base.replace(/\/$/, "");
}

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  auth?: boolean;
};

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = false, headers, ...rest } = options;
  const url = `${getApiBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;

  const requestHeaders = new Headers(headers);
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  if (body !== undefined && !isFormData && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }
  if (body !== undefined && !requestHeaders.has("Accept")) {
    requestHeaders.set("Accept", "application/json");
  }

  if (auth) {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }
    for (const [name, value] of Object.entries(workContextHeaders())) {
      if (!requestHeaders.has(name)) requestHeaders.set(name, value);
    }
  }

  const response = await fetch(url, {
    ...rest,
    headers: requestHeaders,
    body:
      body === undefined
        ? undefined
        : isFormData
          ? (body as FormData)
          : JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && auth) {
      // Ne pas laisser les pages afficher « Unauthenticated. »
      redirectToLoginAfterAuthLoss();
      throw new ApiError("Session expirée. Veuillez vous reconnecter.", 401, payload);
    }

    const validation = firstValidationError(payload);
    const rawMessage =
      validation ??
      (payload && typeof payload === "object" && "message" in payload
        ? String((payload as { message?: string }).message)
        : null) ??
      `Erreur API (${response.status})`;

    throw new ApiError(sanitizeApiErrorMessage(rawMessage) ?? rawMessage, response.status, payload);
  }

  return payload as T;
}
